import { FiSearch } from 'react-icons/fi';

// Shared building blocks for the directory pages (Markets Prices, Facilities
// & Services, Service Professionals, Contact Us) — Figma 481:33123 and siblings.

// Page shell: cream background, Figma's 24px rhythm between blocks
export function DirectoryPage({ children }) {
  return (
    <section className="bg-[#f8f7f2] pb-12 pt-8 sm:pb-16 lg:pt-9">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 sm:px-8 lg:px-10 pub-xl:px-[100px]">
        {children}
      </div>
    </section>
  );
}

// Eyebrow + "Find … in Nigeria." heading + full-width search pill
export function DirectoryHeader({ eyebrow, subject, query, onQueryChange, searchPlaceholder = 'Search by state, species, market, service...' }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <p className="font-oswald text-lg font-normal text-black sm:text-[24px]">{eyebrow}</p>
        <h1 className="font-quattrocento text-2xl font-bold text-black sm:text-3xl lg:text-[32px]">
          Find {subject} in <span className="text-[#e5a81d]">Nigeria</span>.
        </h1>
      </div>

      {onQueryChange && (
        <div className="flex w-full items-center gap-[10px] rounded-full bg-[rgba(0,0,0,0.05)] px-5 py-[15px]">
          <FiSearch className="h-[15px] w-[15px] shrink-0 text-[#a4a4a4]" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label="Search"
            className="min-w-0 flex-1 bg-transparent text-[13px] font-medium text-black outline-none placeholder:text-[#a4a4a4]"
          />
        </div>
      )}
    </div>
  );
}

// "Filter By:" label followed by one or more filter pills
export function FilterBy({ children }) {
  return (
    <div className="flex flex-wrap items-center gap-3 sm:gap-6">
      <span className="whitespace-nowrap text-[16px] font-normal text-[#8e8e8e]">Filter By:</span>
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">{children}</div>
    </div>
  );
}

// One underlined text tab — Figma "Option button": 14px semibold, 50px tall,
// active in #21990f with a 2px rule that sits on the row's 1px grey rule
// (hence -mb-px). Rendered by UnderlineTabs.
export function UnderlineTab({ label, isActive, onClick }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      className={`-mb-px whitespace-nowrap border-b-2 pb-[14px] pt-4 text-[14px] font-semibold leading-[18px] transition-colors ${
        isActive
          ? 'border-[#21990f] text-[#21990f]'
          : 'border-transparent text-black hover:text-[#21990f]'
      }`}
    >
      {label}
    </button>
  );
}

// Tab row, as designed for Livestock Market Prices (Figma 997:57153) and
// shared by every tabbed section: a tab strip up to 982px wide carrying its
// own 1px grey rule, with optional content (filters) outside it on the
// right, bottom-aligned. No overflow scrolling — tabs wrap on phones and
// tablets; from lg up they stay on one line as in Figma, and the filters
// drop below the tabs instead whenever the row runs out of room.
export function UnderlineTabs({ tabs, active, onChange, right }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
      <div
        role="tablist"
        className="flex w-full min-w-0 flex-wrap gap-x-6 border-b border-[#e8e8e8] sm:flex-1 sm:gap-x-[30px] lg:w-auto lg:min-w-max lg:max-w-[982px] lg:flex-nowrap"
      >
        {tabs.map((tab) => (
          <UnderlineTab
            key={tab.key}
            label={tab.label}
            isActive={tab.key === active}
            onClick={() => onChange(tab.key)}
          />
        ))}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </div>
  );
}

// Sub-tab pills, as designed for Livestock Market Prices (Figma 997:56816):
// 13px medium, 12px/8px padding, 6px radius, 0.5px #e8e8e8 border,
// transparent until active, then light green. `singleLine` keeps the pills
// on one row from lg up (Breeding & Genetic Improvement's six pills), only
// scrolling sideways if the window is narrower than the design.
export function PillTabs({ tabs, active, onChange, singleLine = false }) {
  return (
    <div className={`flex flex-wrap items-center gap-2 sm:gap-4 ${singleLine ? 'lg:flex-nowrap lg:overflow-x-auto' : ''}`}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          className={`whitespace-nowrap rounded-[6px] border-[0.5px] border-[#e8e8e8] px-[12px] py-[8px] text-[13px] font-medium leading-4 transition-colors ${
            tab.key === active
              ? 'bg-[rgba(66,234,41,0.1)] text-[#28ba12]'
              : 'bg-transparent text-black hover:bg-white'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function LastUpdated({ date }) {
  return (
    <div className="border-t-[0.55px] border-[#8e8e8e] pt-[10px]">
      <p className="font-urbanist text-[14px] font-medium italic text-[#8e8e8e]">Last Updated: {date}</p>
    </div>
  );
}
